import {
  ACCESS_NP_REVIEW_ROUTE,
  BENEFIT_CHECK_REQUEST_ROUTE,
  CALENDAR_ROUTE,
  CANCELLATION_FEES_ROUTE,
  DOCUMENTS_ROUTE,
  FINANCES_GROUP_ROUTE,
  FOLLOW_UPS_ROUTE,
  GROUP_PRACTICE_PAYER_ELIGIBILITY_ROUTE,
  GROUP_PRACTICE_ROUTE,
  IN_SESSION_TEMPLATES_ROUTE,
  INTERNAL_TOOLS_ROUTE,
  INVOICE_REVIEW_ROUTE,
  MANAGE_PRACTICE_ROUTE,
  MESSAGES_ROUTE,
  NON_APPOINTMENT_HOURS_ROUTE,
  PAYMENTS_ROUTE,
  PERFORMANCE_ROUTE,
  PRESCRIPTION_REVIEW_ROUTE,
  PRESCRIPTION_TASKS_ROUTE,
  RESOURCES_GROUP_ROUTE,
  SETTINGS_ROUTE,
  SUPERVISION_TASKS_ROUTE,
  W9_MANAGEMENT_ROUTE,
} from '@web-app/components/consts';
import { FeatureFlags, NavRoute } from '@web-app/components/Navbar/types';
import { buildUnreadLabRoute } from '@web-app/utils/prescription-review-v2/unread-lab-route';

/**
 * Check if a route should be visible based on feature flags
 */
export const shouldShowRoute = (
  route: NavRoute,
  flags: FeatureFlags,
): boolean => {
  const {
    settingsPageAccess,
    managePracticeDashboard,
    calendarEnabled,
    performanceDashboard,
    showInternalTools,
    isSupervisor,
    supervisionTasks,
    isBerryStreetOnly,
    groupPracticeInvoiceEnabled,
    w9PageEnabled,
    confirmCancellationEnabled,
    additionalBenefitsCheckEnabled,
    chatEnabled,
    subOrgsInvoiceReviewEnabled,
    nonAppointmentHoursEnabled,
    inSessionTemplatesNavEnabled,
    documentsNavEnabled,
  } = flags;

  if (route.featureOwned) return route.path in flags.navEntries;

  switch (route.path) {
    // Synthetic group-wrapper paths don't navigate and have no flag of their
    // own — they're visible whenever at least one child is. The children
    // filtering in NavbarMenuItems/NavbarSkeletons hides the empty group, so
    // the wrapper itself is always allowed here.
    case RESOURCES_GROUP_ROUTE:
    case FINANCES_GROUP_ROUTE:
      return true;
    case CALENDAR_ROUTE:
      return calendarEnabled;
    case SETTINGS_ROUTE:
      return settingsPageAccess;
    case MANAGE_PRACTICE_ROUTE:
      return managePracticeDashboard;
    case FOLLOW_UPS_ROUTE:
      return true;
    case PERFORMANCE_ROUTE:
      return performanceDashboard;
    case INTERNAL_TOOLS_ROUTE:
      return showInternalTools;
    case SUPERVISION_TASKS_ROUTE:
      return isSupervisor && supervisionTasks;
    case PAYMENTS_ROUTE:
      return isBerryStreetOnly;
    case W9_MANAGEMENT_ROUTE:
      return isBerryStreetOnly && w9PageEnabled;
    // INTRO_VIDEO_ROUTE has no flag — visible for everyone (falls through to
    // default). Per-provider access is gated server-side in the upload API.
    case IN_SESSION_TEMPLATES_ROUTE:
      return inSessionTemplatesNavEnabled;
    case DOCUMENTS_ROUTE:
      return documentsNavEnabled;
    case GROUP_PRACTICE_ROUTE:
      return groupPracticeInvoiceEnabled;
    case GROUP_PRACTICE_PAYER_ELIGIBILITY_ROUTE:
      return groupPracticeInvoiceEnabled;
    case CANCELLATION_FEES_ROUTE:
      return confirmCancellationEnabled;
    case BENEFIT_CHECK_REQUEST_ROUTE:
      return additionalBenefitsCheckEnabled;
    case MESSAGES_ROUTE:
      return chatEnabled;
    case INVOICE_REVIEW_ROUTE:
      return subOrgsInvoiceReviewEnabled;
    case NON_APPOINTMENT_HOURS_ROUTE:
      return nonAppointmentHoursEnabled;
    case PRESCRIPTION_REVIEW_ROUTE:
      return flags.isPrescriber;
    case PRESCRIPTION_TASKS_ROUTE:
      return flags.prescriptionTasksEnabled;
    case ACCESS_NP_REVIEW_ROUTE:
      // Assignment-derived visibility (capability fact, like is_prescriber):
      // shown only to providers with assigned ACCESS patients.
      return flags.hasAccessNpReviewQueue;
    default:
      return true;
  }
};

type NotificationRouteSource = {
  route: string;
};

/**
 * First unviewed notification wins per path. Prescription Review follows only
 * the newest unread lab that is on the Awaiting information tab. Lab
 * notifications for patients who are not on that tab do not change the link.
 */
export function buildNavbarNotificationRoutes(
  notifications: readonly NotificationRouteSource[] | undefined,
  latestPatientHealthieId: string | null | undefined,
): Record<string, string> {
  const notificationRoutes: Record<string, string> = {};
  for (const notification of notifications ?? []) {
    const basePath = notification.route.split('?')[0];
    if (basePath === PRESCRIPTION_REVIEW_ROUTE) continue;
    if (!notificationRoutes[basePath]) {
      notificationRoutes[basePath] = notification.route;
    }
  }
  if (latestPatientHealthieId) {
    notificationRoutes[PRESCRIPTION_REVIEW_ROUTE] = buildUnreadLabRoute(
      latestPatientHealthieId,
    );
  }
  return notificationRoutes;
}
